/**
 * The framing, said once, before the drills.
 *
 * Some modules are an argument and the reading IS the module. This one is a
 * set of games, and the writing around it exists only to say why two-foot
 * finishing is worth a session and what the four categories are. That is a
 * thing you read once and never again, so it is a pop-up rather than a first
 * chapter: put it inline and every later visit scrolls past it to reach the
 * drills, which are the point.
 *
 * Once, literally. Dismissal is remembered per module, so returning on a
 * Tuesday to grab a game opens straight onto the games. The header keeps a
 * quiet way back in for the coach who wants to reread it, because "shown
 * once" should never mean "gone".
 *
 * No em dashes.
 */
import React, { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { GOLD } from "../constants/theme";

const ff = "'DM Sans', sans-serif";

const seenKey = (moduleKey) => `bam_module_intro_${moduleKey}`;

/** Has this coach already read the intro for this module? */
export function introSeen(moduleKey) {
  try { return localStorage.getItem(seenKey(moduleKey)) === "1"; } catch { return false; }
}
export function markIntroSeen(moduleKey) {
  try { localStorage.setItem(seenKey(moduleKey), "1"); } catch {}
}

export function ModuleIntro({ intro, moduleKey, C, dark, onDismiss }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onDismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  if (!intro) return null;

  return (
    <div role="dialog" aria-modal="true" aria-label={intro.title}
      style={{ position: "fixed", inset: 0, zIndex: 5900, background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 600, maxHeight: "86vh", overflowY: "auto",
        borderRadius: 18, padding: "26px 28px 28px", fontFamily: ff,
        background: dark ? "#111111" : "#FFFFFF",
        border: `1px solid ${GOLD}33`,
        boxShadow: "0 40px 120px rgba(0,0,0,0.6)" }}>

        <h2 style={{ margin: "0 0 14px", fontSize: 23, lineHeight: 1.22, fontWeight: 900,
          color: C.text, letterSpacing: -.3 }}>
          {intro.title}
        </h2>

        {(intro.body || []).map((p, i) => (
          <p key={i} style={{ margin: i ? "12px 0 0" : 0, fontSize: 14.5, lineHeight: 1.7,
            color: C.textMid }}>
            {p}
          </p>
        ))}

        {intro.points && intro.points.length > 0 && (
          <>
            <div style={{ margin: "20px 0 10px", fontSize: 9.5, fontWeight: 800,
              letterSpacing: 1.4, textTransform: "uppercase", color: GOLD }}>
              {intro.pointsLabel || `In this module, ${intro.points.length} ideas`}
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: "none",
              display: "flex", flexDirection: "column", gap: 9 }}>
              {intro.points.map((pt, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ flexShrink: 0, marginTop: 7, width: 5, height: 5,
                    borderRadius: "50%", background: GOLD }}/>
                  <span style={{ fontSize: 14, lineHeight: 1.6, color: C.textMid }}>{pt}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <button type="button" className="btn" onClick={onDismiss}
          style={{ marginTop: 22, width: "100%", display: "inline-flex", alignItems: "center",
            justifyContent: "center", gap: 7, padding: "13px 18px", borderRadius: 11,
            border: "none", background: GOLD, color: "#111", fontFamily: ff,
            fontSize: 14, fontWeight: 800, cursor: "pointer" }}>
          {intro.cta || "See the drills"} <ArrowRight size={15}/>
        </button>
      </div>
    </div>
  );
}

export default ModuleIntro;
